import { useEffect } from "react";
import { motion } from "framer-motion";
import { Skull, ArrowLeft, User, ShoppingCart } from "lucide-react";
import { useCart } from "@/lib/cart";
import CartDrawer from "@/components/CartDrawer";

export const LegalSection = ({ num, title, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7 }}
    className="border border-blooddeep/50 bg-pulp p-8 sm:p-10"
  >
    <h2 className="font-impact uppercase tracking-widest text-xl sm:text-2xl text-paper mb-6">
      <span className="text-crimson mr-3">{num}</span>
      {title}
    </h2>
    <div className="space-y-4 font-type text-sm text-paper/70 leading-relaxed">{children}</div>
  </motion.section>
);

export const LegalBullet = ({ children }) => (
  <li className="flex gap-3">
    <Skull size={13} className="text-crimson shrink-0 mt-1" />
    <span>{children}</span>
  </li>
);

export default function LegalLayout({ eyebrow, title, accent, updated, children, testId }) {
  const { count, setOpen } = useCart();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid={testId} className="relative min-h-screen bg-void newsprint-grain">
      <div className="absolute inset-0 halftone opacity-[0.06] pointer-events-none" />
      <div className="absolute inset-0 blood-vignette pointer-events-none" />

      <header className="relative border-b border-blooddeep/40 bg-void/85 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" data-testid={`${testId}-brand`} className="flex items-center gap-2 group">
            <img src="/covers/atrocity-logo.webp" alt="Atrocity Comix logo" className="h-6 w-6 rounded-full transition-transform duration-200 group-hover:rotate-12" />
            <span className="font-display text-sm sm:text-base text-paper tracking-wide">
              ATROCITY <span className="text-crimson">COMIX</span>
            </span>
          </a>
          <div className="flex items-center gap-4">
            <a
              href="/"
              data-testid={`${testId}-back-link`}
              className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs text-paper/70 transition-colors duration-150 hover:text-crimson"
            >
              <ArrowLeft size={14} /> Back to the Crypt
            </a>
            <a
              data-testid="nav-account-link"
              href="/account"
              className="p-2 text-paper transition-colors duration-150 hover:text-crimson"
              aria-label="Your account"
            >
              <User size={22} />
            </a>
            <button
              data-testid="nav-cart-trigger"
              onClick={() => setOpen(true)}
              className="relative p-2 text-paper transition-colors duration-150 hover:text-bloodglow"
              aria-label="Open cart"
            >
              <ShoppingCart size={22} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-crimson text-white font-impact text-[10px] w-5 h-5 flex items-center justify-center rounded-full border border-black">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-5xl px-4 sm:px-6 py-16 sm:py-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-marker text-paper text-base sm:text-lg mb-3 -rotate-1 inline-block"
        >
          {eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase text-paper leading-none mb-4"
        >
          {title} <span className="text-crimson">{accent}</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="font-courier text-xs uppercase tracking-widest text-paper/40 mb-14"
        >
          {updated}
        </motion.p>

        <div className="space-y-8">{children}</div>

        <p className="font-courier text-[11px] uppercase tracking-widest text-paper/40 text-center mt-16">
          © 2018 and beyond — Atrocity Comix. All rights reserved.
        </p>
      </main>
      <CartDrawer />
    </div>
  );
}
